import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { ActionItemModel } from '@src/app/modules/actions';

import { ApiService, StoreService } from '../../services';
import { ChatModel, CreateDialogService } from '../../chat';

import { AttachObjectType, PermissionsType } from './types';

@Component({
  selector: 'app-base-chats-list',
  templateUrl: './base-chats-list.component.html',
  styleUrls: ['./base-chats-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseChatsListComponent extends BaseResizableComponent {
  private readonly translateService = inject(TranslateService);
  private readonly apiService = inject(ApiService);
  private readonly store = inject(StoreService);
  private readonly createDialogService = inject(CreateDialogService);

  @Input() title?: string = '';
  @Input({ required: true }) attachObject!: AttachObjectType;
  @Input() permissions?: PermissionsType;

  chats?: ChatModel[];

  openMenu: boolean = false;
  createMenuItems: ActionItemModel[] = [
    {
      title: this.translateService.instant('components.baseChatsList.buttons.createGroupChat'),
      icon: 'users',
      visible: () => this.permissions?.allowGroupChatCreating ?? false,
      action: () => {
        this.openCreateGroupDialog();
      },
    },
  ];

  searchQuery = '';
  searchQuery$ = new BehaviorSubject('');
  searchChats?: ChatModel[];

  loading = false;

  override ngOnInit(): void {
    super.ngOnInit();

    this.store
      .getAttachedChats(this.attachObject.id)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(attachedChats => {
        this.chats = attachedChats.sort((a, b) => (b.chatName > a.chatName ? -1 : 1));
        this.cdr.markForCheck();
      });

    this.getChats();

    this.searchQuery$.pipe(takeUntil(this.destroyed$$)).subscribe(searchQuery => {
      if (searchQuery === '') {
        this.searchChats = undefined;
        return;
      }

      if (this.chats) {
        this.searchChats = [
          ...this.chats.filter(
            chat =>
              chat.chatName.toLowerCase().includes(searchQuery.toLowerCase()) ||
              chat.chatDescription?.toLowerCase().includes(searchQuery.toLowerCase()),
          ),
        ];
      }
    });
  }

  getChats() {
    if (!this.attachObject?.id) {
      return; // TODO: вывести ошибку
    }
    this.apiService.getChatsByAttachId(this.attachObject.id).subscribe();
  }

  searchQueryChangeHandler(searchQuery: string): void {
    this.searchQuery = searchQuery;
    this.searchQuery$.next(searchQuery);
  }

  private openCreateGroupDialog(): void {
    this.createDialogService
      .open(this.translateService.instant('components.baseChatsList.labels.creatingGroupChat'), {
        attachObject: this.attachObject,
      })
      .subscribe(() => {
        this.getChats();
      });
    this.cdr.markForCheck();
  }
}
